import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError, apiErrorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatInrMinor } from '../../lib/constants';
import { AppShell } from '../components/AppShell';
import { ExamFeeNotice } from '../components/ExamFeeNotice';
import {
  createPaymentOrder,
  ensureRegistrationQuotation,
  fetchMyLearners,
  pollPaymentOrderStatus,
  type PaymentOrderResult,
} from '../lib/registration-api';
import { openRazorpayCheckout } from '../lib/razorpay-checkout';
import { childProfilePath, dashboardPath, setLastLearnerId } from '../lib/selected-learner';

interface PaymentLocationState {
  applicationId?: string;
  quotationId?: string | null;
  payableTotalMinor?: string;
}

export function PaymentPage(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { token, loading } = useAuth();
  const state = location.state as PaymentLocationState | null;
  const applicationId = state?.applicationId ?? searchParams.get('applicationId') ?? '';
  const [order, setOrder] = useState<PaymentOrderResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const quotationQuery = useQuery({
    queryKey: ['registration-quotation', applicationId],
    queryFn: () => ensureRegistrationQuotation(applicationId),
    enabled: Boolean(token && applicationId),
    retry: false,
  });

  const quotationId = state?.quotationId ?? quotationQuery.data?.id ?? null;
  const payableMinor = state?.payableTotalMinor ?? quotationQuery.data?.payableTotalMinor ?? null;

  useEffect(() => {
    if (!token || !quotationId || order) return;
    let cancelled = false;
    setBusy(true);
    createPaymentOrder(quotationId)
      .then((result) => {
        if (!cancelled) setOrder(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not start payment');
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, quotationId, order]);

  const examNotice = useMemo(
    () => quotationQuery.data?.snapshot?.notices?.find(
      (notice) => notice.code === 'CAMBRIDGE_EXAM_FEE_NOTE',
    )?.text,
    [quotationQuery.data?.snapshot?.notices],
  );

  if (!loading && !token) return <Navigate to="/login" replace />;
  if (!applicationId) return <Navigate to="/register" replace />;

  if (quotationQuery.isLoading || (quotationQuery.isFetching && !quotationId)) {
    return (
      <AppShell title="Registration fee">
        <p className="text-muted">Loading your registration fee…</p>
      </AppShell>
    );
  }

  if (quotationQuery.isError || !quotationId || payableMinor === null) {
    const message = quotationQuery.error instanceof ApiError
      ? apiErrorMessage(quotationQuery.error.problem)
      : 'Could not load your registration fee quotation.';
    return (
      <AppShell title="Registration fee">
        <div className="error-banner" role="alert">{message}</div>
        <div className="form-actions">
          <Link className="btn btn-secondary" to="/dashboard">Back to dashboard</Link>
        </div>
      </AppShell>
    );
  }

  async function redirectAfterRegistrationPayment(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['my-learners'] });
    await queryClient.invalidateQueries({ queryKey: ['parent-home'] });
    const learners = await fetchMyLearners();
    const learner = learners.find((item) => item.applicationId === applicationId);
    if (learner) {
      setLastLearnerId(learner.learnerId);
      navigate(childProfilePath(learner.learnerId));
      return;
    }
    navigate(dashboardPath());
  }

  async function waitForCapture(orderId: string): Promise<void> {
    const registrationSettled = async (): Promise<boolean> => {
      const learners = await fetchMyLearners();
      const learner = learners.find((item) => item.applicationId === applicationId);
      return Boolean(
        learner && learner.applicationStatus !== 'REGISTRATION_PENDING_PAYMENT',
      );
    };

    try {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const status = await pollPaymentOrderStatus(orderId);
        if (status.status === 'CAPTURED' || await registrationSettled()) {
          await redirectAfterRegistrationPayment();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (await registrationSettled()) {
        await redirectAfterRegistrationPayment();
        return;
      }
      setError('Payment is still processing. Check your dashboard in a moment.');
    } catch (err) {
      if (await registrationSettled().catch(() => false)) {
        await redirectAfterRegistrationPayment();
        return;
      }
      setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not confirm payment');
    } finally {
      setConfirming(false);
    }
  }

  async function payNow(): Promise<void> {
    if (!order) return;
    setBusy(true);
    setError(null);
    try {
      await openRazorpayCheckout({
        order,
        description: 'Registration fee',
        onSuccess: () => {
          setConfirming(true);
          void waitForCapture(order.paymentOrderId);
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment could not start');
    } finally {
      setBusy(false);
    }
  }

  const isFree = BigInt(payableMinor) === 0n;

  return (
    <AppShell title="Registration fee">
      <ol className="progress-rail">
        <li className="progress-step" data-complete="true">
          <span className="progress-step-dot" aria-hidden="true">
            ✓
          </span>
          <span className="progress-step-label">Verify email</span>
        </li>
        <span className="progress-connector" aria-hidden="true" />
        <li className="progress-step" data-complete="true">
          <span className="progress-step-dot" aria-hidden="true">
            ✓
          </span>
          <span className="progress-step-label">Learner details</span>
        </li>
        <span className="progress-connector" aria-hidden="true" />
        <li className="progress-step" aria-current="step">
          <span className="progress-step-dot" aria-hidden="true">
            3
          </span>
          <span className="progress-step-label">Registration fee</span>
        </li>
      </ol>

      <div className="page-header">
        <p className="lede">
          A one-time fee to complete your child&apos;s registration. Subject fees are quoted
          separately once your subject choices have been approved.
        </p>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="card card-flush">
        <div className="card-body">
          <table className="summary-table">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Registration fee</td>
                <td className="money">{formatInrMinor(payableMinor)}</td>
              </tr>
            </tbody>
          </table>

          <div className="summary-total" aria-live="polite">
            <span className="summary-total-label">Total payable</span>
            <span className={`summary-total-value${isFree ? ' summary-free' : ''}`}>
              {isFree ? 'No fee' : formatInrMinor(payableMinor)}
            </span>
          </div>
        </div>
      </div>

      <div className="section-gap">
        <ExamFeeNotice text={examNotice} />
      </div>

      {confirming ? (
        <div className="notice notice-info section-gap" role="status">
          <strong>Confirming your payment</strong>
          <p>This usually takes a few seconds. Please do not close this window.</p>
        </div>
      ) : (
        <div className="form-actions">
          <Link className="btn btn-ghost" to="/dashboard">
            Back to dashboard
          </Link>
          <button
            className="btn btn-lg"
            type="button"
            onClick={payNow}
            disabled={busy || !order}
          >
            {busy ? 'Opening checkout…' : `Pay ${formatInrMinor(payableMinor)}`}
          </button>
        </div>
      )}
    </AppShell>
  );
}
